import {
  Button,
  Caption,
  css,
  FormField,
  HelpIcon,
  InfoIcon,
  Inline,
  SelectInput,
  styled,
  TextInput,
} from 'canoe';
import { Field, FieldAttributes, Form, FormikContextType, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ContentType } from '@candu-ai/content/types';
import {
  ContentPlacement,
  LogicOptionItem,
  logicOptions,
  LogicOptions,
  ModalTimeFrames,
  PageOptionItem,
  pageOptions,
  PageOptions,
  PlacementUrl,
  positioningOptions,
  showUntilOptions,
  triggerModalOptions,
  URLOptions,
} from '@candu-ai/placement/types';
import { getDefaultOptionList, getNewOption, getUrlErrors } from '@candu-ai/placement/utils';

import { LegacyUrls } from './LegacyUrls';
import { MultiplePagesInput } from './MultiplePagesInput';

const Info = styled(Caption)`
  display: flex;
`;

const Link = styled.a`
  ${({ theme }) => css`
    color: ${theme.colors.BrandPurple};
  `}
`;

const ButtonContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    margin-top: ${theme.spaces.s};

    button {
      margin-left: ${theme.spaces.s};
    }
  `}
`;

export interface SelectorFormProps {
  exists?: boolean;
  onCancel?: () => void;
  actionButtons?: JSX.Element;
  formikContext?: FormikContextType<ContentPlacement>;
}

export const SelectorForm = React.memo(
  ({ onCancel, exists, actionButtons, formikContext }: SelectorFormProps) => {
    const formikFromContext = useFormikContext<ContentPlacement>();

    // this is needed to work for nested forms like the edit placement modal in the Placements tab,
    // otherwise validation doesn't work
    const {
      values,
      setFieldValue,
      setValues,
      dirty,
      isValid,
      errors,
      touched,
      setFieldTouched,
      setTouched,
    } = formikContext || formikFromContext;

    const [optionList, setOptionList] = useState<PageOptions | undefined>(
      getDefaultOptionList(values),
    );

    useEffect(() => {
      // if there is no matching option in values on first load, default to "Match Any"
      if (!values.urlsLogicOperator) {
        setFieldValue('urlsLogicOperator', LogicOptions.ANY);
      }
    }, [setFieldValue, values.urlsLogicOperator]);

    useEffect(() => {
      if (!dirty) {
        setOptionList(getDefaultOptionList(values));
      }
    }, [dirty, values]);

    const onOptionChange = (option: PageOptionItem | null) => {
      if (!option) {
        return;
      }
      const newPlacementRules = getNewOption(option.value, values);

      if (newPlacementRules !== null) {
        const { urls, urlsLogicOperator } = newPlacementRules;
        setTouched({ urls: false, urlsLogicOperator: false }, false);
        setValues({ ...values, urls, urlsLogicOperator });
      }

      setOptionList(option.value);
    };

    const onUpdateUrls = (newUrls: PlacementUrl[], resetTouched = false) => {
      setFieldTouched('urls', !resetTouched, !resetTouched);
      setFieldValue('urls', newUrls);
    };

    const deleteDenyURL = useCallback(
      (index: number) => {
        if (!values.denyURLs) {
          return;
        }

        const denyURLs = [...values.denyURLs];
        denyURLs.splice(index, 1);

        setFieldValue('denyURLs', denyURLs);

        // if there are no denyURLs left and no urls exist, set empty array and switch to "On all pages"
        if (!denyURLs.length && !values.urls?.length) {
          setFieldValue('urls', []);
          setFieldValue('urlsLogicOperator', LogicOptions.ALL);
        }
      },
      [values, setFieldValue],
    );

    const canSave = dirty && isValid;

    const urlErrors = useMemo(() => getUrlErrors(errors, touched), [errors, touched]);
    const isModal = values.type === ContentType.Modal;
    const isTour = values.type === ContentType.Tour;
    const isInline = values.type === ContentType.Inline;

    useEffect(() => {
      if (isTour && !values.showTimeframe) {
        setFieldValue('showTimeframe', ModalTimeFrames.UNTIL_DISMISSED, false);
      }
    }, [isTour, values.showTimeframe, setFieldValue]);

    const hasWildcard = values.urls?.some((url) => url.operation.type === URLOptions.REGEX);

    return (
      <Form data-cy="form-placement">
        {isModal && (
          <>
            <Field name="trigger">
              {({ meta }: FieldAttributes<any>) => (
                <FormField
                  data-cy="trigger"
                  label="When will the Overlay be displayed?"
                  state={meta.error ? 'danger' : 'none'}
                  feedback={meta.error || ''}
                >
                  <Inline>
                    <SelectInput
                      selectedItem={triggerModalOptions[0]}
                      items={triggerModalOptions}
                      // eslint-disable-next-line @typescript-eslint/no-empty-function
                      onSelect={() => {}}
                      clearable={false}
                    />
                  </Inline>
                </FormField>
              )}
            </Field>

            <Field name="showTimeframe">
              {({ meta }: FieldAttributes<any>) => (
                <FormField
                  data-cy="showTimeframe"
                  label="How often?"
                  state={meta.error ? 'danger' : 'none'}
                  feedback={meta.error || ''}
                >
                  <Inline>
                    <SelectInput
                      selectedItem={showUntilOptions.find(
                        (item) => item.value === values.showTimeframe,
                      )}
                      items={showUntilOptions}
                      onSelect={(selected) => setFieldValue('showTimeframe', selected?.value)}
                      clearable={false}
                    />
                  </Inline>
                </FormField>
              )}
            </Field>
          </>
        )}

        <FormField
          data-cy="urls"
          label="Where in your product?"
          description={
            <Inline align="center" space="xs">
              <span>
                Target pages where this Candu content should appear. Use a combination of URL rules
                to get as targeted as you need.
              </span>
              <Inline align="center" space="xs" wrap="nowrap">
                <HelpIcon color="OnyxGrayL30" size="s" />
                <span>
                  For help with page targeting, see this{' '}
                  <Link
                    href="https://docs.candu.ai/en/articles/5916841-launching-candu-content#h_575628d267"
                    target="_blank"
                  >
                    guide
                  </Link>
                  .
                </span>
              </Inline>
            </Inline>
          }
          state={urlErrors || errors.urlsLogicOperator ? 'danger' : 'none'}
          feedback={urlErrors || errors.urlsLogicOperator || ''}
        >
          <>
            {hasWildcard && (
              <Info>
                Use <InfoIcon />
                <Link
                  href="https://docs.candu.ai/en/articles/6004622-url-embedding"
                  target="_blank"
                >
                  URL wildcards
                </Link>
                &nbsp;to control where your Content appears.
              </Info>
            )}
            <Inline marginBottom="xs" space="xxs">
              <SelectInput<PageOptionItem>
                clearable={false}
                selectedItem={pageOptions.find((option) => option.value === optionList)}
                items={pageOptions}
                onSelect={onOptionChange}
              />
              {optionList === PageOptions.MULTI_PAGE && (
                <SelectInput<LogicOptionItem>
                  clearable={false}
                  selectedItem={logicOptions.find(
                    (option) => option.value === values.urlsLogicOperator,
                  )}
                  items={logicOptions}
                  onSelect={(selectedLogicOption) => {
                    setFieldValue('urlsLogicOperator', selectedLogicOption?.value);
                    setFieldTouched('urlsLogicOperator', true);
                  }}
                />
              )}
            </Inline>

            {optionList === PageOptions.MULTI_PAGE && (
              <MultiplePagesInput
                urls={values.urls || []}
                updateUrls={onUpdateUrls}
                urlErrors={urlErrors}
              />
            )}
          </>
        </FormField>

        <LegacyUrls placement={values} deleteDenyURL={deleteDenyURL} />

        {isInline && (
          <>
            <Field name="selector">
              {({ meta, field }: FieldAttributes<any>) => {
                return (
                  <FormField
                    data-cy="selector"
                    label="Where on the page?"
                    necessity="required"
                    state={meta.error ? 'danger' : 'none'}
                    feedback={meta.error || ''}
                  >
                    <>
                      <Info>
                        Pick an HTML selector, such as #home-page-top-bar. <InfoIcon />{' '}
                        <Link
                          href="https://docs.candu.ai/en/articles/6013105-define-a-selector-manually"
                          target="_blank"
                        >
                          Learn more about HTML selectors
                        </Link>
                      </Info>

                      <TextInput
                        {...field}
                        data-cy="formfield-selector-child"
                        value={field.value}
                        placeholder="div#banner"
                        state={meta.error ? 'danger' : 'none'}
                      />
                    </>
                  </FormField>
                );
              }}
            </Field>

            <Field name="location">
              {({ meta }: FieldAttributes<any>) => (
                <FormField
                  label="Positioning"
                  necessity="required"
                  state={meta.error ? 'danger' : 'none'}
                  feedback={meta.error || ''}
                >
                  <SelectInput
                    selectedItem={positioningOptions.find((item) => item.value === values.location)}
                    items={positioningOptions}
                    onSelect={(selected) => setFieldValue('location', selected?.value)}
                    clearable={false}
                  />
                </FormField>
              )}
            </Field>
          </>
        )}

        <ButtonContainer>
          {actionButtons || (
            <>
              {exists && (
                <Button
                  data-cy="cancel-edit"
                  onClick={onCancel}
                  variant="neutral"
                  appearance="outline"
                >
                  Cancel
                </Button>
              )}
              <Button
                data-cy="save-placement"
                disabled={!canSave}
                type="submit"
                variant="secondary"
              >
                Save
              </Button>
            </>
          )}
        </ButtonContainer>
      </Form>
    );
  },
);
